/**
 * Generates chunks ahead of the rider in a Web Worker and hands them to ChunkedTerrain. The terrain still
 * generates synchronously when asked for a chunk that has not arrived, so results never depend on timing.
 */
import type { ChunkData, ChunkTrees } from './chunkgen';
import type { ChunkedTerrain } from './chunks';
import type { LevelId } from './levels';
import { Heightfield } from './terrain';

interface Reply {
  ci: number; cj: number; trees: ChunkTrees | null;
  field: { cells: number; cellSize: number; originX: number; originZ: number; heights: Float32Array; surfaces: Uint8Array };
}

const toChunk = (r: Reply): ChunkData => {
  const f = new Heightfield(r.field.cells, r.field.cellSize, r.field.originX, r.field.originZ);
  f.heights.set(r.field.heights);
  f.surfaces.set(r.field.surfaces);
  return { ci: r.ci, cj: r.cj, field: f, trees: r.trees };
};

/**
 * Generates the given chunks on a few workers at once (the start area at load: a second or more of main-thread
 * generation otherwise). Same generateChunk, so the result is identical to generating inline.
 */
export async function generateInWorkers(levelId: LevelId, chunks: readonly (readonly [number, number])[], workers = Math.max(1, Math.min(4, (navigator.hardwareConcurrency ?? 4) - 1))): Promise<ChunkData[]> {
  const queue = [...chunks], out: ChunkData[] = [];
  const run = async () => {
    const w = new Worker(new URL('./chunkWorker.ts', import.meta.url), { type: 'module' });
    try {
      for (let next = queue.shift(); next; next = queue.shift()) {
        const [ci, cj] = next;
        out.push(toChunk(await new Promise<Reply>((ok, no) => {
          w.onmessage = (e: MessageEvent<Reply>) => ok(e.data);
          w.onerror = (e) => no(new Error(`chunk worker failed: ${e.message}`));
          w.postMessage({ levelId, ci, cj });
        })));
      }
    } finally { w.terminate(); }
  };
  await Promise.all(Array.from({ length: Math.min(workers, chunks.length) }, run));
  return out;
}

export class ChunkPrefetcher {
  private readonly worker: Worker;
  private readonly pending = new Set<string>();

  constructor(private readonly terrain: ChunkedTerrain, private readonly levelId: LevelId, readonly radius = 290) {
    this.worker = new Worker(new URL('./chunkWorker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (e: MessageEvent<Reply>) => {
      const r = e.data;
      this.pending.delete(`${r.ci},${r.cj}`);
      this.terrain.adopt(toChunk(r));
    };
  }

  /** Queues the chunks around (x, z) that are neither loaded nor already requested. At most two in flight. */
  update(x: number, z: number): void {
    if (this.pending.size >= 2) return;
    for (const [ci, cj] of this.terrain.missing(x, z, this.radius)) {
      const k = `${ci},${cj}`;
      if (this.pending.has(k)) continue;
      this.pending.add(k);
      this.worker.postMessage({ levelId: this.levelId, ci, cj });
      if (this.pending.size >= 2) return;
    }
  }
}
